import type { NextLinkProps } from "@project/ui";
import { Grid, GridItem, NextLink, Icon } from "@project/ui";
import { gridGap, gridTemplateColumns } from "@project/ui/src/theme/global-styles";
import type { ModuleQuickLinksFieldsFragment } from "@/components/contentful/module-quick-links/__generated/module-quick-links.contentful.generated";
import { LinkWrapper } from "@/components/link-wrapper";
import { Title } from "@/components/title";
import { Description } from "@/components/description";
import { ModuleSpacer } from "@/components/module-spacer";

export type ModuleQuickLinksProps = ModuleQuickLinksFieldsFragment & {
    isEmbedded?: boolean;
};

const QuickLink = ({ children, isExternal, ...props }: NextLinkProps) => (
    <NextLink
        {...props}
        p={4}
        _hover={{ backgroundColor: "rgba(0,0,0,0.1)" }}
        display="flex"
        gap={2}
        justifyContent="space-between"
        h="full"
        alignItems="center"
        rounded="medium"
        transitionProperty="var(--transition-property-common)"
        transitionDuration="var(--transition-duration-moderate)"
        fontSize={{ base: "mobileTextMedium", md: "desktopTextMedium" }}
        lineHeight={{ base: "mobileTextMedium", md: "desktopTextMedium" }}
        fontWeight={400}
        {...(isExternal ? { target: "_blank", rel: "noopener noreferrer" } : {})}
    >
        {children}
        <Icon name={isExternal ? "external" : "arrow-right"} />
    </NextLink>
);

const ModuleQuickLinks = ({
    title,
    description,
    linksCollection,
    isEmbedded,
}: ModuleQuickLinksProps) => {
    return (
        <ModuleSpacer {...(isEmbedded && { wrapperProps: { px: { base: 5, md: 10, l: 0 } } })}>
            <Grid templateColumns={gridTemplateColumns} gap={gridGap} alignItems="start">
                <GridItem colStart={1} colEnd={isEmbedded ? 13 : { base: 3, l: 4 }}>
                    <Title>{title}</Title>
                    <Description>{description}</Description>
                </GridItem>

                <GridItem
                    colStart={isEmbedded ? 1 : { base: 1, l: 4 }}
                    colEnd={isEmbedded ? 13 : { base: 3, l: 10 }}
                >
                    <Grid
                        templateColumns="repeat(2, 1fr)"
                        gridAutoRows="1fr"
                        rowGap={0}
                        columnGap={gridGap}
                    >
                        {linksCollection?.items.map(
                            (link) =>
                                link && (
                                    <GridItem colSpan={{ base: 2, l: 1 }} key={link.sys.id}>
                                        <LinkWrapper
                                            item={link}
                                            renderAs={QuickLink}
                                            renderExternalLinkAs={QuickLink}
                                            isExternal={link.__typename === "ExternalLink"}
                                        />
                                    </GridItem>
                                )
                        )}
                    </Grid>
                </GridItem>
            </Grid>
        </ModuleSpacer>
    );
};

export { ModuleQuickLinks };
